import {
  type BookLanguages,
  bookDirection,
  bookFace,
  ENGLISH,
} from '@app/studio'
import { useEffect, useMemo, useRef } from 'react'
import { anchorFromSelection, rangeForAnchor } from './anchor-dom'
import { type GoTo, glide } from './go-to'
import { ReaderSection } from './ReaderSection'
import {
  Book,
  type CommentMarking,
  Comments,
  type Composed,
  type NoteRendering,
  Notes,
} from './reader-context'
import { readerAttrs, useReaderSettings } from './reader-settings'
import './reader.css'

export { InlineRun } from './InlineRun'
export type { CommentMarking, Writing } from './reader-context'
export { BookProvider, WritingProvider } from './reader-context'

/** The book's own name, with each written line taking its direction from itself. */
export function BookTitlePage({
  title,
  subtitle,
}: {
  title: string
  subtitle?: string | null
}) {
  return (
    <section className="book-titlepage">
      <h1 dir="auto">{title}</h1>
      {subtitle ? (
        <p className="book-titlepage__subtitle" dir="auto">
          {subtitle}
        </p>
      ) : null}
    </section>
  )
}

/**
 * A book, read on screen.
 *
 * The same content the book is set from, and NOT the same layout: a page has a
 * trim, a foot and a gutter, and a screen has a column and a scroll. What is
 * shared is the data and the meaning of every mark — a Hebrew run is Hebrew in
 * both, and a note is a note in both.
 *
 * Where they part is the note. On paper the choice is foot or back, and both
 * are compromises with a page that cannot be clicked. Here a note opens where
 * it stands, so neither compromise is inherited.
 */
export function BookReader({
  title,
  subtitle,
  sections,
  bodies,
  markers,
  onReveal,
  /** A note the list asked for: the text goes to the call that made it. */
  calling,
  comments = { counts: new Map() },
  /**
   * What the book is written in, which is what lays the column out.
   *
   * The book's, never a run's: a run still carries its own language and takes
   * its own face and isolation from it, one layer down. This decides which way
   * the column reads and what most of its words are set in.
   */
  book = ENGLISH,
  /** What the book is set in, which is the book's own and reaches print. */
  typeface,
}: {
  /** What the book is called, which leads it here as it does on the page. */
  title: string
  subtitle?: string | null
  sections: Composed
  comments?: CommentMarking
  calling?: GoTo
  book?: BookLanguages
  typeface?: 'sans' | 'serif' | null
} & NoteRendering) {
  const { settings } = useReaderSettings()
  const rendering = useMemo(
    () => ({ bodies, markers, onReveal }),
    [bodies, markers, onReveal],
  )
  const ref = useRef<HTMLElement>(null)
  /** Every marked phrase on screen, so a click on one can find its thread. */
  const hits = useRef<{ id: string; blockId: string; range: Range }[]>([])
  const revealed = comments.revealed
  const { anchors, onAnchorsDrawn, onSelect, active, onOpenThread } = comments

  /* The band asked for a passage, so the passage comes to the reader — the
     point of reading a comment beside the text is having both in front of you. */
  useEffect(() => {
    const id = revealed?.id
    if (!id) return
    const block = ref.current?.querySelector(`[data-block="${CSS.escape(id)}"]`)
    if (block instanceof HTMLElement) bringIntoView(block)
  }, [revealed])

  /*
   * A note read in the list, found in the text.
   *
   * The marker takes a reader to the note; this is the same journey back, and
   * both exist because a note and the sentence that called it are read
   * together — a list of notes with no way to the calls is a list of answers to
   * questions somewhere else.
   */
  useEffect(() => {
    const id = calling?.id
    if (!id) return
    const call = ref.current?.querySelector(
      `[data-noteref="${CSS.escape(id)}"]`,
    )
    if (call instanceof HTMLElement) bringIntoView(call)
  }, [calling])

  /*
   * The words each thread is about, drawn over the text.
   *
   * Through the Custom Highlight API rather than by wrapping the words in a
   * span: a highlight crosses the element boundaries a remark does not care
   * about — a phrase running from an English run into a Hebrew one — and it
   * marks the rendered text without altering it, which is what lets the
   * presentation stay computed. Where the browser has no such API there is no
   * highlight and everything else still works.
   */
  useEffect(() => {
    const root = ref.current
    /* The passages are read rather than merely awaited: what is drawn hangs off
       the text on screen, so a book that changes redraws, and one with
       nothing in it has nowhere to draw. */
    if (!root || !anchors || !sections.length) return
    /*
     * Whether the words can be FOUND is asked whatever the browser can paint.
     *
     * Resolving an anchor is a search through the passage's text; painting one
     * needs the Custom Highlight API. Tying the two together made a browser
     * without it report that no anchor resolved, which the band reads as every
     * remark having lost its words — a missing highlight becoming a false
     * statement about the text.
     */
    const canPaint = !!CSS.highlights
    const drawn = new Highlight()
    /* The thread in hand, drawn over the top of the rest — a separate highlight
       rather than a stronger range inside the same one, because one highlight
       paints one way whatever it holds. */
    const inHand = new Highlight()
    inHand.priority = 1
    const found = new Set<string>()
    const marked: { id: string; blockId: string; range: Range }[] = []
    for (const [blockId, list] of anchors) {
      const block = root.querySelector(`[data-block="${CSS.escape(blockId)}"]`)
      if (!block) continue
      for (const { id, anchor } of list) {
        const range = rangeForAnchor(block, anchor)
        if (!range) continue
        drawn.add(range)
        if (id === active) inHand.add(range)
        found.add(id)
        marked.push({ id, blockId, range })
      }
    }
    if (canPaint) {
      CSS.highlights.set(COMMENT_HIGHLIGHT, drawn)
      CSS.highlights.set(ACTIVE_HIGHLIGHT, inHand)
    }
    /* Kept, because a highlight cannot be clicked: it paints the text without
       putting anything in the tree, so which thread a click landed in has to be
       answered from the ranges themselves. */
    hits.current = marked
    onAnchorsDrawn?.(found)
    return () => {
      if (canPaint) {
        CSS.highlights.delete(COMMENT_HIGHLIGHT)
        CSS.highlights.delete(ACTIVE_HIGHLIGHT)
      }
      hits.current = []
    }
  }, [anchors, onAnchorsDrawn, sections, active])

  /*
   * The marked words, clicked.
   *
   * Answered from the ranges rather than from an element, because that is all
   * there is: the highlight paints the text and puts nothing in the tree to
   * carry a handler. So the caret position under the pointer is found and tested
   * against each range.
   *
   * A click that ENDS A SELECTION is not this gesture — somebody who just
   * dragged across a phrase is choosing words to write about, and jumping them
   * to a thread would take away what they were doing.
   */
  useEffect(() => {
    const root = ref.current
    if (!root || !onOpenThread) return
    const reached = (e: MouseEvent) => {
      if (!document.getSelection()?.isCollapsed) return
      const at = caretAt(e.clientX, e.clientY)
      if (!at) return
      for (const { id, blockId, range } of hits.current) {
        /* `comparePoint` throws rather than answering when the node is not in
           its tree, which one stale range would otherwise turn into a dead
           handler for every live one beside it. */
        try {
          if (range.comparePoint(at.node, at.offset) === 0) {
            onOpenThread(id, blockId)
            return
          }
        } catch {}
      }
    }
    root.addEventListener('click', reached)
    return () => root.removeEventListener('click', reached)
  }, [onOpenThread])

  /*
   * What the reader has selected, read once they stop selecting.
   *
   * On the document rather than on the article, because a drag that ends
   * outside the text is still a selection inside it, and a pointer released
   * over the band would otherwise leave the last words held for ever.
   */
  useEffect(() => {
    if (!onSelect) return
    const read = () => {
      const root = ref.current
      onSelect(root ? anchorFromSelection(root, document.getSelection()) : null)
    }
    document.addEventListener('pointerup', read)
    document.addEventListener('keyup', read)
    return () => {
      document.removeEventListener('pointerup', read)
      document.removeEventListener('keyup', read)
    }
  }, [onSelect])

  return (
    <Book.Provider value={book}>
      <Notes.Provider value={rendering}>
        <Comments.Provider value={comments}>
          <article
            /* `book` is the artifact scope: the shared sheet's faces, ranks and
               marks apply inside it, and `reader` is the screen's own layout —
               the column, the margin the marks hang in, and the scroll. */
            className="reader book"
            ref={ref}
            lang={book.primary}
            dir={bookDirection(book)}
            data-face={bookFace(book)}
            /* The reader's own preference where they have one, and the book's
               otherwise. Only the book's reaches a printed page. */
            data-typeface={
              (settings.face === 'book' ? typeface : settings.face) ?? undefined
            }
            /* How this person reads: the size, the leading and the measure.
               Read here rather than passed in, so every surface that renders a
               book honors it without being told to — the shared copy included,
               where the reader has no account and the device is the answer. */
            {...readerAttrs(settings)}
          >
            {/* The book names itself before its first words. The chrome above
                says where you are; this says what you are reading. */}
            <BookTitlePage title={title} subtitle={subtitle} />
            {/* A book with nothing in it yet is still the book: its title
                page, and one quiet line where the first words will go. */}
            {sections.length === 0 ? (
              <p className="reader__empty" lang="en" dir="ltr">
                Nothing is written here yet.
              </p>
            ) : (
              sections.map((s) => <ReaderSection key={s.id} section={s} />)
            )}
          </article>
        </Comments.Provider>
      </Notes.Provider>
    </Book.Provider>
  )
}

/**
 * Center something in the page, arriving with a short scroll.
 *
 * The page is what the text scrolls in, so the target is worked out in page
 * coordinates rather than handed to `scrollIntoView` — which would animate the
 * whole distance, and across a book that is the page flying past while nothing
 * can be read.
 */
function bringIntoView(el: HTMLElement) {
  const box = el.getBoundingClientRect()
  const to = box.top + window.scrollY - (window.innerHeight - box.height) / 2
  glide(
    () => window.scrollY,
    (top, smooth) =>
      window.scrollTo({ top, behavior: smooth ? 'smooth' : 'auto' }),
    Math.max(0, to),
  )
}

/**
 * The caret position under a point, across the two spellings browsers have.
 *
 * `caretPositionFromPoint` is the standard and `caretRangeFromPoint` is what
 * WebKit shipped; neither is everywhere, and a click on marked words is a
 * convenience, so where there is neither there is simply no jump.
 */
function caretAt(x: number, y: number): { node: Node; offset: number } | null {
  type WithCaret = Document & {
    caretPositionFromPoint?: (
      x: number,
      y: number,
    ) => { offsetNode: Node; offset: number } | null
    caretRangeFromPoint?: (x: number, y: number) => Range | null
  }
  const d = document as WithCaret
  const spot = d.caretPositionFromPoint?.(x, y)
  if (spot) return { node: spot.offsetNode, offset: spot.offset }
  const range = d.caretRangeFromPoint?.(x, y)
  if (range) return { node: range.startContainer, offset: range.startOffset }
  return null
}

/** The one highlight name the band's marks are drawn under. */
const COMMENT_HIGHLIGHT = 'studio-comment'
/** The thread the reader is on, drawn over the others. */
const ACTIVE_HIGHLIGHT = 'studio-comment-active'
